import { isUserLoggedIn, getSession } from '@skyscanner/session-validation';
import { jwtDecode as decodeJwt } from 'jwt-decode';
import { SingleValueCookie } from 'saddlebag-cookie';
import { LocalStorage } from 'saddlebag-localstorage';

const LOGGED_OUT_UTID_COOKIE_KEY = 'traveller_context';
const CSRF_TOKEN_COOKIE_KEY = '__Secure-sid_csrf_token';
const ID_TOKEN_COOKIE_KEY = 'sid_id_token';
const LOCAL_STORAGE_NAMESPACE = 'SID';
const CSRF_TOKEN_KEY = 'csrfToken';
const TRAVELLER_AUTHENTICATION_STATE = Object.freeze({
  UNSET: 0,
  UNAUTHENTICATED: 1,
  AUTHENTICATED: 2,
});
const IS_LOGGED_IN_TIMEOUT = 15000;
const PROFILE_PICTURE_CLAIM = 'picture';

const getLocalStorage = () => new LocalStorage(LOCAL_STORAGE_NAMESPACE);

/* isLoggedIn is a function which return a promise that resolves to a boolean.
  The current implementation first checks the cookies to identify if the user is
  logged in or not. If the user is not logged in it returns `false` right away without
  an API call, otherwise it makes an API call to validate the session of the user. */
const isLoggedIn = (timeoutDuration = IS_LOGGED_IN_TIMEOUT) => {
  const timeout = new Promise((resolve) => {
    setTimeout(() => resolve(false), timeoutDuration);
  });
  return Promise.race([isUserLoggedIn(), timeout]);
};

/**
 * @deprecated Use getAccountId() for authenticated users. getUtid() will be
 * removed once all consumers have migrated. See utid->account_id migration plan:
 * https://skyscanner.atlassian.net/wiki/x/soEElQ
 * @returns {string|undefined} utid from session or traveller_context cookie
 */
const getUtid = () => {
  const session = getSession();
  const sidUtid = session && session.utid;
  return (
    sidUtid || new SingleValueCookie(LOGGED_OUT_UTID_COOKIE_KEY).getValue()
  );
};

// account_id is only present for authenticated sessions (sourced from sid_id_token initially, falling back to sid_session_token if not present).
// Returns undefined for anonymous users — do not use absence of account_id as an
// authentication check; use isLoggedIn() for that.
const getAccountId = () => {
  const session = getSession();
  return session?.accountId;
};

const getCsrfToken = () => {
  const localStorage = getLocalStorage();
  /* __Secure-sid_csrf_token cookie is unavailable in dev as __Secure cookies only
    available under HTTPS. The LocalStorage copy is used as a fallback for dev. */
  return (
    new SingleValueCookie(CSRF_TOKEN_COOKIE_KEY).getValue() ||
    localStorage.tryGetValue(CSRF_TOKEN_KEY, false, false)
  );
};

const getProfilePicture = () => {
  try {
    const idToken = new SingleValueCookie(ID_TOKEN_COOKIE_KEY).getValue();
    if (!idToken) {
      return null;
    }
    const claims = decodeJwt(idToken);
    return claims[PROFILE_PICTURE_CLAIM] || null;
  } catch (e) {
    return null;
  }
};

export {
  isLoggedIn,
  getUtid,
  getAccountId,
  TRAVELLER_AUTHENTICATION_STATE,
  getCsrfToken,
  getProfilePicture,
};
